import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './LiveInterviewQuestionGateModal.css';

/**
 * Keep a two-way interview move pending while its job questions are prepared.
 *
 * This component is deliberately presentation-only: the workspace owns all
 * Firebase writes, generation requests, approval hashes, and the final move.
 */
const LiveInterviewQuestionGateModal = ({
  gate,
  onCancel,
  onGenerate,
  onConfirmMove,
  onEdit,
  onSaveEdit,
  onCancelEdit,
  onQuestionChange
}) => {
  const { t } = useTranslation('applicants');
  const firstQuestionRef = useRef(null);
  const busy = gate?.phase === 'generating' || gate?.phase === 'moving';
  const reviewing = gate?.phase === 'review' || gate?.phase === 'moving';
  const visibleQuestions = gate?.editing ? (gate?.draftQuestions || []) : (gate?.questions || []);
  const cancelCurrentState = gate?.editing ? onCancelEdit : onCancel;

  useEffect(() => {
    if (gate?.editing) firstQuestionRef.current?.focus();
  }, [gate?.editing]);

  useEffect(() => {
    if (!gate) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !busy) cancelCurrentState();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [busy, cancelCurrentState, gate]);

  if (!gate) return null;

  const candidateCount = gate.context?.applicants?.length || 1;

  return (
    <div className="live-question-gate-overlay" role="presentation">
      <section
        className="live-question-gate"
        role="dialog"
        aria-modal="true"
        aria-labelledby="live-question-gate-title"
        dir={gate.isRTL ? 'rtl' : 'ltr'}
        onMouseDown={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="live-question-gate__header">
          <div className="live-question-gate__identity">
            <span className="live-question-gate__icon" aria-hidden="true">
              <i className="fa-solid fa-robot" />
            </span>
            <div>
              <p>{t('workspace.questionGate.eyebrow')}</p>
              <h2 id="live-question-gate-title">
                {gate.editing
                  ? t('workspace.questionGate.editTitle')
                  : reviewing
                    ? t('workspace.questionGate.reviewTitle')
                    : t('workspace.questionGate.generateTitle')}
              </h2>
            </div>
          </div>
          <button
            type="button"
            className="live-question-gate__close"
            onClick={cancelCurrentState}
            disabled={busy}
            aria-label={t('workspace.questionGate.close')}
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </header>

        <div className="live-question-gate__body">
          <div className="live-question-gate__job">
            <span>{t('workspace.questionGate.forJob')}</span>
            <strong>{gate.context?.jobTitle || t('workspace.questionGate.thisJob')}</strong>
          </div>

          {!reviewing ? (
            <>
              <div className="live-question-gate__message">
                {gate.phase === 'generating' ? (
                  <>
                    <span className="live-question-gate__spinner" aria-hidden="true">
                      <i className="fa-solid fa-spinner fa-spin" />
                    </span>
                    <div>
                      <h3>{t('workspace.questionGate.generatingTitle')}</h3>
                      <p>{t('workspace.questionGate.generatingBody')}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="live-question-gate__notice-icon" aria-hidden="true">
                      <i className="fa-solid fa-wand-magic-sparkles" />
                    </span>
                    <div>
                      <h3>{t('workspace.questionGate.confirmGenerationTitle')}</h3>
                      <p>{t('workspace.questionGate.confirmGenerationBody', { count: candidateCount })}</p>
                    </div>
                  </>
                )}
              </div>
              {gate.error && <p className="live-question-gate__error" role="alert">{gate.error}</p>}
            </>
          ) : (
            <>
              <p className="live-question-gate__intro">
                {gate.editing
                  ? t('workspace.questionGate.editHint')
                  : t('workspace.questionGate.reviewHint')}
              </p>
              <div className="live-question-gate__questions">
                {visibleQuestions.map((question, index) => {
                  const inputId = `live-question-gate-question-${index}`;
                  return (
                  <div className="live-question-gate__question" key={question.id || index}>
                    {gate.editing ? (
                      <>
                        <label htmlFor={inputId}>{t('workspace.questionGate.questionNumber', { number: index + 1 })}</label>
                        <textarea
                          id={inputId}
                          ref={index === 0 ? firstQuestionRef : undefined}
                          value={question.text}
                          onChange={(event) => onQuestionChange(index, event.target.value)}
                          onDragStart={(event) => event.stopPropagation()}
                          draggable={false}
                          rows={3}
                          maxLength={600}
                        />
                      </>
                    ) : (
                      <>
                        <span>{t('workspace.questionGate.questionNumber', { number: index + 1 })}</span>
                        <p>{question.text}</p>
                      </>
                    )}
                  </div>
                  );
                })}
              </div>
              {gate.error && <p className="live-question-gate__error" role="alert">{gate.error}</p>}
            </>
          )}
        </div>

        <footer className="live-question-gate__footer">
          {reviewing && gate.editing ? (
            <>
              <button
                type="button"
                className="live-question-gate__button live-question-gate__button--secondary"
                onClick={onCancelEdit}
                disabled={busy}
              >
                {t('workspace.questionGate.cancel')}
              </button>
              <button
                type="button"
                className="live-question-gate__button live-question-gate__button--primary"
                onClick={onSaveEdit}
                disabled={busy || !visibleQuestions.some((question) => String(question?.text || '').trim())}
              >
                {t('workspace.questionGate.saveQuestions')}
              </button>
            </>
          ) : reviewing ? (
            <>
              <button
                type="button"
                className="live-question-gate__button live-question-gate__button--secondary"
                onClick={onEdit}
                disabled={busy}
              >
                <i className="fa-solid fa-pen" />
                {t('workspace.questionGate.editQuestions')}
              </button>
              <button
                type="button"
                className="live-question-gate__button live-question-gate__button--primary"
                onClick={onConfirmMove}
                disabled={busy || !visibleQuestions.some((question) => String(question?.text || '').trim())}
              >
                {gate.phase === 'moving' && <i className="fa-solid fa-spinner fa-spin" />}
                {gate.phase === 'moving'
                  ? t('workspace.questionGate.moving')
                  : t('workspace.questionGate.confirmAndMove')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="live-question-gate__button live-question-gate__button--secondary"
                onClick={onCancel}
                disabled={busy}
              >
                {t('workspace.questionGate.cancel')}
              </button>
              <button
                type="button"
                className="live-question-gate__button live-question-gate__button--primary"
                onClick={onGenerate}
                disabled={busy}
              >
                {busy && <i className="fa-solid fa-spinner fa-spin" />}
                {busy
                  ? t('workspace.questionGate.generating')
                  : t('workspace.questionGate.generateQuestions')}
              </button>
            </>
          )}
        </footer>
      </section>
    </div>
  );
};

export default LiveInterviewQuestionGateModal;
